/**
 * THE CONFIRMATION GATE.
 *
 * design-intelligence §2.3 permits exactly two writes inside a chat turn
 * (`create_task`, `snooze_contact` — both touch only the user's own queue, both
 * reversible in a tap). Everything that could reach another human comes back as
 * a PROPOSAL and needs a second, human-initiated call. This card is that call's
 * only UI.
 *
 * ## Four rules this component exists to keep
 *
 *  1. **Never auto-confirm.** There is no `autoConfirm` prop, no effect that
 *     fires `confirm`, and no keyboard shortcut bound to it. A proposal sits at
 *     `proposed` until a person presses a button whose label says what will
 *     happen.
 *  2. **Show the payload before the buttons.** A gate that hides what it is
 *     gating is a consent dialog in name only, so the due date, the channel, the
 *     subject and the whole body are rendered above the actions. The body is
 *     rendered VERBATIM in a `<pre>`-styled block: a message the user will put
 *     their name to must be shown exactly as it will be saved, whitespace
 *     included.
 *  3. **Say what confirming does, in the user's terms.** "Saves the message as a
 *     draft you can edit and send yourself. Nothing is sent until you tap send."
 *     — not "executes `draft_message`".
 *  4. **A decided proposal is a receipt, not a control.** Once `status` leaves
 *     `proposed` the buttons are gone entirely (not disabled) and the card states
 *     the outcome, because a greyed-out Approve next to something already
 *     approved reads as "it failed".
 *
 * ## Expiry
 *
 * `expiresAt` is on the wire because a proposal nobody looked at must not
 * execute a month later. The card shows the deadline while it is live and
 * refuses the action once it has passed — the server would reject it anyway, and
 * a button that is guaranteed to fail is exactly what this sweep removed
 * elsewhere.
 */

import type { ChatProposedActionDto } from '@crm/contracts';
import { Badge, Button, badgeVariants, cn } from '@crm/ui';
import { Link } from '@tanstack/react-router';
import { CheckIcon, ShieldQuestionIcon, XIcon } from 'lucide-react';
import { formatDateTime, relativeTime } from '../../lib/format.ts';
import { actionEffect, actionTitle } from './chat-vocabulary.ts';

/** Reads a string off the opaque payload without pretending it is typed. */
function str(payload: Record<string, unknown>, key: string): string | null {
  const value = payload[key];
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

export type ProposalCardProps = {
  readonly action: ChatProposedActionDto;
  readonly onDecide: (decision: 'confirm' | 'cancel') => void;
  readonly pending?: boolean;
  readonly now?: Date;
};

export function ProposalCard({
  action,
  onDecide,
  pending = false,
  now = new Date(),
}: ProposalCardProps) {
  const payload = action.payload as Record<string, unknown>;
  const expired = Date.parse(action.expiresAt) <= now.getTime();
  const live = action.status === 'proposed' && !expired;

  const dueAt = str(payload, 'dueAt');
  const channel = str(payload, 'channel');
  const subject = str(payload, 'subject');
  const body = str(payload, 'body');
  const goal = str(payload, 'goal');
  const reason = str(payload, 'reason');

  return (
    <section
      data-testid="chat-proposal"
      data-action-id={action.id}
      data-status={expired && action.status === 'proposed' ? 'expired' : action.status}
      className={cn(
        'rounded-lg border p-3 text-sm',
        live ? 'border-primary/50 bg-primary/[0.04]' : 'border-border bg-muted/30',
      )}
    >
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <ShieldQuestionIcon className="size-4 text-primary" aria-hidden />
        <span className="font-medium">{actionTitle(action.kind)}</span>
        {action.contactName === null ? null : action.contactId === null ? (
          <Badge variant="secondary">{action.contactName}</Badge>
        ) : (
          <Link
            to="/contacts/$contactId"
            params={{ contactId: action.contactId }}
            className={cn(badgeVariants({ variant: 'secondary' }), 'hover:underline')}
          >
            {action.contactName}
          </Link>
        )}
        {channel === null ? null : <Badge variant="muted">{channel}</Badge>}
      </div>

      <dl className="mt-2 flex flex-col gap-1.5">
        {dueAt === null ? null : (
          <div className="flex flex-wrap gap-x-2">
            <dt className="text-muted-foreground">When</dt>
            <dd>
              {formatDateTime(dueAt) ?? dueAt}
              <span className="ml-1 text-xs text-muted-foreground">
                ({relativeTime(dueAt, now) ?? 'no date'})
              </span>
            </dd>
          </div>
        )}
        {reason === null ? null : (
          <div className="flex flex-wrap gap-x-2">
            <dt className="text-muted-foreground">Why</dt>
            <dd>{reason}</dd>
          </div>
        )}
        {goal === null ? null : (
          <div className="flex flex-wrap gap-x-2">
            <dt className="text-muted-foreground">Goal</dt>
            <dd>{goal}</dd>
          </div>
        )}
        {subject === null ? null : (
          <div className="flex flex-wrap gap-x-2">
            <dt className="text-muted-foreground">Subject</dt>
            <dd className="font-medium">{subject}</dd>
          </div>
        )}
      </dl>

      {body === null ? null : (
        <p className="mt-2 whitespace-pre-wrap rounded-md border border-border bg-background p-2 text-sm">
          {body}
        </p>
      )}

      {live ? (
        <>
          <p className="mt-2 text-xs text-muted-foreground">{actionEffect(action.kind)}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" disabled={pending} onClick={() => onDecide('confirm')}>
              <CheckIcon aria-hidden />
              {action.kind === 'draft_message' ? 'Save the draft' : 'Add the follow-up'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={pending}
              onClick={() => onDecide('cancel')}
            >
              <XIcon aria-hidden />
              No thanks
            </Button>
            <span className="self-center text-xs text-muted-foreground">
              Expires {relativeTime(action.expiresAt, now) ?? 'soon'}
            </span>
          </div>
        </>
      ) : (
        <p className="mt-2 text-xs text-muted-foreground">{outcomeText(action.status, expired)}</p>
      )}
    </section>
  );
}

/**
 * The receipt line.
 *
 * `expired` is checked BEFORE `status`, because the server leaves a lapsed row at
 * `proposed` until a sweep touches it — so a card that read its status alone
 * would keep offering an approval the server will refuse.
 */
function outcomeText(status: ChatProposedActionDto['status'], expired: boolean): string {
  if (status === 'confirmed') {
    return 'You approved this. It is in your queue.';
  }
  if (status === 'cancelled') {
    return 'You declined this. Nothing was saved.';
  }
  if (expired || status === 'expired') {
    return 'This offer expired before it was approved, so nothing happened. Ask again to get a fresh one.';
  }
  return 'Nothing has happened yet.';
}
